import difference from 'lodash/difference';
import isNil from 'lodash/isNil';

import {
  DeliverableAttachmentType
} from 'types';

interface PartialAttachment {
  id: any,
  meta: {
    type: DeliverableAttachmentType
  },
  url: string,
  relatedAssets?: null | Array<number>,
  linked?: Array<PartialAttachment>
}

interface Input<T extends PartialAttachment> {
  prevAttachments: Array<T>,
  nextAttachments: Array<T>
}

interface Output<T extends PartialAttachment> {
  addAttachments: Array<T>,
  deleteAttachments: Array<number>,
  linkedAttachments: Array<{
    parentAssetId: any,
    linked: Array<T>
  }>,
  updateAttachments: Array<{
    assetId: any,
    data: T
  }>
}
/**
 * The only purpose of this function is to remove `relatedAssets` from resulting structure
 */
function deleteRelatedAssets<T extends PartialAttachment>(toCleanUp: Output<T>): Output<T> {
  return {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addAttachments: toCleanUp.addAttachments.map(({ relatedAssets, ...att }) => ({
      ...att
    })) as Array<any>,
    deleteAttachments: toCleanUp.deleteAttachments,
    linkedAttachments: toCleanUp.linkedAttachments.map(att => ({
      parentAssetId: att.parentAssetId,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      linked: att.linked.map(({ relatedAssets, ...link }) => ({
        ...link
      })) as Array<any>
    })),
    updateAttachments: toCleanUp.updateAttachments.map(att => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { relatedAssets, ...rest } = att.data;

      return {
        assetId: att.assetId,
        data: rest
      } as any;
    })
  };
}

/**
 *
 * Please, please, please! Don't change this function without changing the related tests.
 *
 */
export function prepareStoryDataForBE<T extends PartialAttachment>({
  prevAttachments: prevAttachment,
  nextAttachments: nextAttachment
}: Input<T>): Output<T> {
  const linked = nextAttachment
    .reduce((acc, val) => {
      const prev = prevAttachment.find(a => a.id === val.id) ?? null;

      if (prev === null) {
        return acc;
      }

      const diff = difference(val.relatedAssets ?? [], prev.relatedAssets ?? []);

      if (diff.length > 0) {
        const linkedAttachments = nextAttachment.filter(att => diff.includes(att.id as number));

        if (linkedAttachments.length > 0) {
          acc.push({
            parentAssetId: val.id as number,
            linked: linkedAttachments
          });
        }
      }

      return acc;
    }, [] as Output<T>['linkedAttachments']);
  const update = nextAttachment.reduce((acc, val) => {
    const prev = prevAttachment.find(a => (
      val.id === a.id && val.url !== a.url
    )) ?? null;

    if (prev === null) {
      return acc;
    }

    acc.push({
      assetId: prev.id as number,
      data: val
    });

    return acc;
  }, [] as Output<T>['updateAttachments']);
  const newAttachments = nextAttachment
    .filter(
      att => !prevAttachment.map(a => a.id).includes(att.id)
        && !update.map(val => val.data.id).includes(att.id as number)
        && att.meta.type === 'highres'
    )
    .map(att => {
      if (isNil(att.relatedAssets)) {
        return att;
      }

      const linkedAttachments = nextAttachment
        .filter(a => (att.relatedAssets ?? []).includes(a.id));

      if (linkedAttachments.length > 0) {
        return {
          ...att,
          linked: linkedAttachments
        };
      }

      return att;
    });
  const deleted = prevAttachment
    .filter(
      att => !nextAttachment.map(a => a.id).includes(att.id)
        && !update.map(val => val.assetId).includes(att.id as number)
    )
    .map(att => att.id) as Array<number>;


  return deleteRelatedAssets({
    addAttachments: newAttachments.sort((a, b) => a.id - b.id),
    deleteAttachments: deleted,
    linkedAttachments: linked,
    updateAttachments: update
  });
}
